import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Template } from '../models';
import { templateService, TemplateWeDMSStatus } from '../services/documentService';

export interface TemplatePdfState {
  pdfUrl: string | null;
  isLoading: boolean;
  error: string | null;
  hasPdf: boolean;
  wedmsStatus: 'pending' | 'processing' | 'completed' | 'failed' | null;
  isPolling: boolean;
}

/**
 * 템플릿 PDF 로딩 공통 훅
 * - WeDMS 연동된 템플릿인 경우 PDF URL 반환
 * - CORS 우회를 위해 DAS 백엔드를 통해 프록시
 * - WeDMS 처리 중일 때 자동 폴링하여 상태 업데이트
 */
export const useTemplatePdfViewModel = (template: Template | null | undefined) => {
  const queryClient = useQueryClient();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [state, setState] = useState<TemplatePdfState>({
    pdfUrl: null,
    isLoading: false,
    error: null,
    hasPdf: false,
    wedmsStatus: null,
    isPolling: false,
  });

  // template.id와 wedms_doc_id, wedms_status를 개별 값으로 추출
  const templateId = template?.id;
  const wedmsDocId = template?.wedms_doc_id;
  const wedmsStatus = template?.wedms_status;

  // WeDMS 상태 폴링 함수
  const pollWeDMSStatus = useCallback(async () => {
    if (!templateId) return;

    try {
      const status: TemplateWeDMSStatus = await templateService.getWeDMSStatus(templateId);

      if (status.status === 'completed' && status.wedms_doc_id) {
        // 완료됨 - 폴링 중지 및 템플릿 목록 새로 고침
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // 템플릿 쿼리 무효화하여 새로 고침
        queryClient.invalidateQueries({ queryKey: ['templates'] });

        const url = templateService.getPdfPreviewUrl(templateId);
        setState({
          pdfUrl: url,
          isLoading: false,
          error: null,
          hasPdf: true,
          wedmsStatus: 'completed',
          isPolling: false,
        });
      } else if (status.status === 'failed') {
        // 실패함 - 폴링 중지
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setState({
          pdfUrl: null,
          isLoading: false,
          error: status.wedms_error || 'WeDMS 처리에 실패했습니다.',
          hasPdf: false,
          wedmsStatus: 'failed',
          isPolling: false,
        });
      } else {
        // 아직 처리 중
        setState((prev) => ({
          ...prev,
          wedmsStatus: status.status,
          isPolling: true,
        }));
      }
    } catch (error: unknown) {
      // 401 에러 시 폴링 중단 (인증 만료)
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 401) {
          console.warn('Authentication expired, stopping WeDMS status polling');
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setState((prev) => ({
            ...prev,
            isPolling: false,
            error: '인증이 만료되었습니다. 다시 로그인해주세요.',
          }));
          return;
        }
      }
      console.error('WeDMS 상태 확인 실패:', error);
    }
  }, [templateId, queryClient]);

  // 폴링 시작/중지 관리
  useEffect(() => {
    // 폴링 정리
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!templateId) {
      setState({
        pdfUrl: null,
        isLoading: false,
        error: null,
        hasPdf: false,
        wedmsStatus: null,
        isPolling: false,
      });
      return;
    }

    // WeDMS 연동 완료된 경우
    if (wedmsDocId) {
      const url = templateService.getPdfPreviewUrl(templateId);
      setState({
        pdfUrl: url,
        isLoading: false,
        error: null,
        hasPdf: true,
        wedmsStatus: 'completed',
        isPolling: false,
      });

      // 폴링 중지
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // 처리 중인 경우 폴링 시작
    if (wedmsStatus === 'processing' || wedmsStatus === 'pending') {
      setState({
        pdfUrl: null,
        isLoading: false,
        error: null,
        hasPdf: false,
        wedmsStatus: wedmsStatus,
        isPolling: true,
      });

      // 이미 폴링 중이면 스킵
      if (!pollingIntervalRef.current) {
        // 즉시 한 번 체크
        pollWeDMSStatus();

        // 3초마다 폴링
        pollingIntervalRef.current = setInterval(pollWeDMSStatus, 3000);
      }
      return;
    }

    // 실패한 경우
    if (wedmsStatus === 'failed') {
      setState({
        pdfUrl: null,
        isLoading: false,
        error: template?.wedms_error || 'WeDMS 처리에 실패했습니다.',
        hasPdf: false,
        wedmsStatus: 'failed',
        isPolling: false,
      });
      return;
    }

    // WeDMS 연동되지 않은 경우 (wedmsStatus 없음)
    setState({
      pdfUrl: null,
      isLoading: false,
      error: 'WeDMS에 연동되지 않은 템플릿입니다. 템플릿을 다시 업로드해주세요.',
      hasPdf: false,
      wedmsStatus: null,
      isPolling: false,
    });
  }, [templateId, wedmsDocId, wedmsStatus, template?.wedms_error, pollWeDMSStatus]);

  const reload = useCallback(() => {
    if (template?.wedms_doc_id) {
      const url = templateService.getPdfPreviewUrl(template.id);
      setState({
        pdfUrl: url,
        isLoading: false,
        error: null,
        hasPdf: true,
        wedmsStatus: 'completed',
        isPolling: false,
      });
    } else if (template?.id) {
      // 강제 상태 확인
      pollWeDMSStatus();
    }
  }, [template, pollWeDMSStatus]);

  return {
    ...state,
    reload,
  };
};

export default useTemplatePdfViewModel;
